/* ==== band-b.js ==== */
/* ============================================================
   BAND B — Session 3's counter work (#ppb → #box).

   Liquid figures.

   The digits do not count. They are the number, from the moment
   you see them. The only motion is the level rising through the
   glyphs once, behind a wave that travels exactly as long as the
   fill does and then stops.

   Almost all of it is CSS: --odo-fill and --odo-phase are registered
   custom properties with a transition and a keyframe on them, so
   the browser interpolates both. This file only decides *when*,
   and builds the one element CSS cannot conjure — the liquid
   layer, which needs the same glyphs a second time.

   Owns [data-odo]. app.js still owns [data-count] and is
   untouched; the attribute is the handover, so neither system can
   reach the other's elements.
   ============================================================ */
(() => {
'use strict';

const nodes = [...document.querySelectorAll('[data-odo]')];
if (!nodes.length) return;

/* The liquid is a second copy of the glyphs stacked on the first.
   It is aria-hidden because the real text is already on the parent
   and a screen reader should hear the figure once, not twice. Only
   after it exists does the parent dim to the empty vessel colour —
   until then the parent is the whole figure, which is what keeps
   this correct with JS blocked. */
nodes.forEach(el => {
  const liq = document.createElement('span');
  liq.className = 'odo__liq';
  liq.setAttribute('aria-hidden', 'true');
  liq.textContent = el.textContent;
  el.appendChild(liq);
  el.classList.add('is-lined');
  el._liq = liq;
});

if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

/* Emptying them is itself a change to --fill, so it would animate:
   the figure would spend 1.8s draining from full before it ever
   filled. Suppress the transition for that one step, flush the
   style, then hand it back. The reflow read is load-bearing —
   without it the browser coalesces both writes into one frame,
   never sees the empty state, and nothing transitions afterwards. */
nodes.forEach(el => {
  el._liq.style.transition = 'none';
  /* -8%, not 0%: the wave wobbles ±0.06em around the waterline, so a level
     parked exactly at the bottom lets crests peek above the floor and light
     the low-hanging comma while the digits are still empty. Parked a step
     below, empty is empty. */
  el._liq.style.setProperty('--odo-fill', '-8%');
});
void document.body.offsetHeight;
nodes.forEach(el => { el._liq.style.transition = ''; });

function fill(el) {
  if (el._filled) return;
  el._filled = true;
  el.classList.add('is-filling');          /* starts the wave */
  el._liq.style.setProperty('--odo-fill', '100%');
}

const io = new IntersectionObserver((es) => {
  es.forEach(e => { if (e.isIntersecting) { fill(e.target); io.unobserve(e.target); } });
}, { threshold: 0.6 });
nodes.forEach(el => io.observe(el));

/* The same safety net app.js carries, for the same reason:
   IntersectionObserver does not fire in a hidden document, so a
   background-tab load would leave these figures sitting empty —
   and empty, here, means a headline number in 13% ink. */
function sweep() {
  const vh = innerHeight;
  nodes.forEach(el => {
    if (el._filled) return;
    const r = el.getBoundingClientRect();
    if (r.top < vh && r.bottom > 0) { fill(el); io.unobserve(el); }
  });
}
addEventListener('visibilitychange', () => { if (!document.hidden) sweep(); });
addEventListener('load', () => setTimeout(sweep, 1200));

})();
